// Subset latin only (cobre acentos PT-BR em U+0000-00FF); evita embarcar
// cyrillic/greek/vietnamese/etc no bundle e no precache do service worker.
import "@fontsource/roboto/latin-300.css";
import "@fontsource/roboto/latin-400.css";
import "@fontsource/roboto/latin-500.css";
import "@fontsource/roboto/latin-700.css";
import "@fontsource/ibm-plex-sans/latin-400.css";
import "@fontsource/ibm-plex-sans/latin-500.css";
import "@fontsource/ibm-plex-sans/latin-600.css";
import "@fontsource/ibm-plex-sans/latin-700.css";

import * as api from "./redux/api/actions.js";

import { AVAILABLE_ENTITIES, utils } from "./database";
import React, { useEffect, Suspense } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { withRouter } from "./utils/withRouterCompat";
import { getLoggedUser, login } from "./redux/auth/actions.js";

// Theme global (A.4.1, MUI v5 migration): exportado de src/theme.js e
// provido em src/index.js via <ThemeProvider>. App ja recebe theme do
// pai, sem precisar criar/usar useTheme aqui.

// ============================================================================
// HYBRID LAZY LOADING STRATEGY (Sprint 2)
// ============================================================================
// Monitor para instrumentação e debugging de lazy loading
import { monitorLazyLoad, setupNetworkMonitoring } from "./utils/lazyLoadMonitor";

// ========== PÁGINAS ADMIN (LAZY) ==========
// Páginas administrativas que NÃO precisam funcionar offline
// OK falhar quando offline - usuário receberá Error Boundary
const AdminAddTypeDomain = monitorLazyLoad(() => import("./pages/AdminAddTypeDomain/AdminAddTypeDomain.js"), 'AdminAddTypeDomain');
const AdminAddValueDomain = monitorLazyLoad(() => import("./pages/AdminAddValueDomain/AdminAddValueDomain.js"), 'AdminAddValueDomain');
const AdminReceiptReleases = monitorLazyLoad(() => import("./pages/AdminReceiptReleases/AdminReceiptReleases.js"), 'AdminReceiptReleases');
const AdminReleasesList = monitorLazyLoad(() => import("./pages/AdminReleasesList/AdminReleasesList.js"), 'AdminReleasesList');
const ObservabilityDashboard = monitorLazyLoad(
  () => import('./pages/ObservabilityDashboard/ObservabilityDashboard.js'),
  'ObservabilityDashboard'
);

// ========== PÁGINAS CORE (EAGER) ==========
// Páginas essenciais que DEVEM funcionar 100% OFFLINE
// CRÍTICO: Carregadas imediatamente para garantir disponibilidade offline

// Batches (Lotes) - CORE offline
import Batch from "./pages/batches/Batch.js";
import BatchCreate from "./pages/batches/Create.js";
import BatchMerge from "./pages/batches/Merge.js";
import BatchUpdate from "./pages/batches/Update.js";

// Bulls (Touros) - CORE offline
import Bull from "./pages/bulls/Bull.js";
import BullCreate from "./pages/bulls/Create.js";
import BullCreateForAll from "./pages/bulls/CreateForAll.js";
import BullList from "./pages/bulls/List.js";
import BullUpdate from "./pages/bulls/Update.js";

// Corrals (Retiros) - CORE offline
import Corral from "./pages/corrals/Corral";
import CorralCreate from "./pages/corrals/Create.js";
import CorralUpdate from "./pages/corrals/Update.js";

// IATF - CORE da aplicação (usado offline no curral)
import D0 from "./pages/IATF/D0";
import D0AfterResync from "./pages/IATF/D0AfterResync";
import D0IATF from "./pages/IATF/D0IATF";
import D0Resync from "./pages/IATF/D0Resync";
import DG from "./pages/IATF/DG";
import DGFinal from "./pages/IATF/DGFinal";
import DGRessinc from "./pages/IATF/DGRessinc";
import DN from "./pages/IATF/DN";
import IATF from "./pages/IATF/IATF";
import Resync from "./pages/IATF/Resync";

// Estação de Monta - CORE offline
import EstacaodeMonta from "./pages/EstacaoDeMontaOtimizada";

// Farms (Fazendas) - CORE offline
import Farm from "./pages/farms/Farm.js";
import FarmCreate from "./pages/farms/Create.js";
import FarmUpdate from "./pages/farms/Update";

// Partidas - CORE offline
import Partidas from "./pages/partidas/Partida.js";

// Protocols (Protocolos) - CORE offline
import Protocol from "./pages/protocols/Protocol";
import ProtocolCreate from "./pages/protocols/Create";
import ProtocolUpdate from "./pages/protocols/Update";
import ProtocolsList from "./pages/protocols/List";

// Home - CORE offline
import Home from "./pages/Home";

// ========== PÁGINAS OPCIONAIS (LAZY) ==========
// Páginas que NÃO precisam funcionar offline
// Carregadas sob demanda para otimizar bundle inicial

// Importação de dados (admin only)
const ImportData = monitorLazyLoad(() => import("./pages/ImportData/ImportData"), 'ImportData');

// Relatórios (não usados no curral)
const NewReport = monitorLazyLoad(() => import("./pages/new-reports/NewReport"), 'NewReport');
const Report = monitorLazyLoad(() => import("./pages/reports/Report"), 'Report');

// Treinamentos (opcional)
const Trainings = monitorLazyLoad(() => import("./pages/Trainings/Training.js"), 'Trainings');

// Dashboard Geral (online-only)
const DashboardGeral = monitorLazyLoad(() => import("./pages/DashboardGeral/DashboardGeral.js"), 'DashboardGeral');

// Usuários (geralmente gerenciados online)
const UserCreate = monitorLazyLoad(() => import("./pages/Users/Create.js"), 'UserCreate');
const UserList = monitorLazyLoad(() => import("./pages/Users/List.js"), 'UserList');
const UserPreRegister = monitorLazyLoad(() => import("./pages/Users/PreRegisterUser.js"), 'UserPreRegister');
const UserUpdate = monitorLazyLoad(() => import("./pages/Users/Update.js"), 'UserUpdate');

// Perfil do usuário (read-only — funciona offline)
const UserProfile = monitorLazyLoad(() => import("./pages/profile/UserProfile"), 'UserProfile');

import { Alert } from '@mui/material';
import CssBaseline from "@mui/material/CssBaseline";
import LoadingOverlay from "./components/LoadingOverlay";
import LazyErrorBoundary from "./components/LazyErrorBoundary";
import Prompt from "./components/Prompt.js";
import OfflineIndicator from "./components/OfflineIndicator";
import AppShell from "./components/layout/AppShell";
import { TextField } from "@mui/material";

import { connect } from "react-redux";
import { verify } from "./utils/verifyDuplicatePages";

// v3 (shell architecture, 2026-05-10): RootContainer + Content styled
// components + drawerWidth foram removidos. AppShell agora orquestra
// AppSidebar + TopActionBar + PageHeader. SyncView migrou pra dentro
// do TopActionBar.

function App({ logged, title, ...props }) {
  const location = useLocation();
  const [newPassword, setNewPassword] = React.useState("");

  // Inicializar monitor de lazy loading
  useEffect(() => {
    setupNetworkMonitoring();

    // Expor função de stats no console para debugging
    console.log('[APP] Lazy Load Stats disponíveis via: window.__LAZY_LOAD_STATS_LOG__()');

    return () => {
      // Cleanup se necessário
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <div className="app-wrapper-bg">
      <LoadingOverlay />
      <OfflineIndicator />
      <CssBaseline />
      <AppShell>
              {/* Error Boundary para recuperação de falhas de chunk lazy */}
              <LazyErrorBoundary>
                {/* Suspense para lazy loading de rotas */}
                <Suspense fallback={<LoadingOverlay />}>
                  <Routes>
                {/* Home / Farm List */}
                <Route path="/" element={<Home />} />
                {/* Dashboard Geral Tecnico (online-only) */}
                <Route path="/dashboard-geral" element={<DashboardGeral />} />
                {/* Estação de monta otimizada */}
                <Route path="/farms/estacaodemonta"
                  element={<EstacaodeMonta />}
                />
                {/* Farms */}
                <Route path="/farms/create" element={<FarmCreate />} />
                <Route path="/farms/:id" element={<Farm />} />
                <Route path="/farms/:id/update" element={<FarmUpdate />} />
                <Route path="/farms/:farmId/report" element={<NewReport />} />
                <Route path="/farms/:farmId/new-report"
                  element={<Report />}
                />
                {/* Users */}
                <Route path="/users" element={<UserList />} />
                <Route path="/users/create" element={<UserCreate />} />
                <Route path="/users/PreRegister" element={<UserPreRegister />} />
                <Route path="/users/:id" element={<UserUpdate />} />
                {/* Perfil do usuário */}
                <Route path="/perfil" element={<UserProfile />} />
                {/* Protocols */}
                <Route path="/protocols" element={<ProtocolsList />} />
                <Route path="/protocols/create"
                  element={<ProtocolCreate />}
                />
                <Route path="/protocols/:id" element={<Protocol />} />
                <Route path="/protocols/:id/update"
                  element={<ProtocolUpdate />}
                />
                {/* Bulls */}
                <Route path="/farms/:id/bulls" element={<BullList />} />
                <Route path="/farms/:id/bulls/create"
                  element={<BullCreate />}
                />
                <Route path="/farms/:id/bulls/:bullId" element={<Bull />} />
                <Route path="/farms/:id/bulls/:bullId/update"
                  element={<BullUpdate />}
                />
                <Route path="/bulls/create-for-all/batch/:batchId/management/:management"
                  element={<BullCreateForAll />}
                />
                {/* Corrals */}
                <Route path="/farms/:farmId/corrals/create"
                  element={<CorralCreate />}
                />
                <Route path="/farms/:farmId/corrals/:id"
                  element={<Corral />}
                />
                <Route path="/farms/:farmId/corrals/:id/update"
                  element={<CorralUpdate />}
                />
                <Route path="/farms/:farmId/corrals/:corralId/report"
                  element={<NewReport />}
                />
                <Route path="/farms/:farmId/corrals/:corralId/new-report"
                  element={<Report />}
                />
                {/* Batches */}
                <Route path="/farms/:farmId/corrals/:corralId/mergeBatches"
                  element={<BatchMerge />}
                />
                <Route path="/farms/:farmId/corrals/:corralId/batches/:id/update"
                  element={<BatchUpdate />}
                />
                <Route path="/farms/:farmId/corrals/:corralId/batches/create"
                  element={<BatchCreate />}
                />
                <Route path="/farms/:farmId/corrals/:corralId/batches/:id"
                  element={<Batch />}
                />
                <Route path="/farms/:farmId/corrals/:corralId/batches/:batchId/report"
                  element={<NewReport />}
                />
                <Route path="/farms/:farmId/corrals/:corralId/batches/:batchId/new-report"
                  element={<Report />}
                />
                {/* Cadastro de partidas */}
                <Route path="/partidas/:bullId/batch/:batchId/management/:management"
                  element={<Partidas />}
                />
                {/* Importar dados */}
                <Route path="/importar-dados" element={<ImportData />} />
                {/* Treinamentos */}
                <Route path="/treinamentos" element={<Trainings />} />
                {/* Administrador lancamentos*/}
                <Route path="/lancamentos-do-administrador"
                  element={<AdminReleasesList />}
                />
                {/* Adicionar Valor Dominio */}
                <Route path="/lancamentos-de-valores-dominio"
                  element={<AdminAddValueDomain />}
                />
                {/* Adicionar Tipo Dominio */}
                <Route path="/lancamentos-de-tipos-dominio"
                  element={<AdminAddTypeDomain />}
                />
                {/* Recuperar dados do comprovante */}
                <Route path="/lancamentos-de-comprovantes"
                  element={<AdminReceiptReleases />}
                />
                {/* Observabilidade (admin only — guard dentro da page) */}
                <Route path="/observabilidade" element={<ObservabilityDashboard />} />
                {/* Processes */}
                <Route path="/D0IATF/:id" element={<D0IATF />} />
                <Route path="/D0IATF/:id/redirected/:redirected"
                  element={<D0IATF />}
                />
                <Route path="/D0/:id" element={<D0 />} />
                <Route path="/D0Resync/:id" element={<D0Resync />} />
                <Route path="/D0AfterResync/:id"
                  element={<D0AfterResync />}
                />
                <Route path="/DN/:id" element={<DN />} />
                <Route path="/DG/:id/" element={<DG />} />
                <Route path="/DGRessinc/:id/" element={<DGRessinc />} />
                <Route path="/DGFinal/:id" element={<DGFinal />} />
                <Route path="/IATF/:id" element={<IATF />} />
                <Route path="/IATF/:id/redirected/:redirected"
                  element={<IATF />}
                />
                <Route path="/Resync/:id" element={<Resync />} />
                <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
                </Suspense>
              </LazyErrorBoundary>
      </AppShell>
          <Prompt
            visible={!props?.loggedIn && props?.loginError === "Senha incorreta!"}
            title="Detectamos uma mudança na senha associada à sua conta em outro dispositivo. Para continuar desfrutando dos nossos serviços, solicitamos que insira a sua senha atual no campo abaixo."
            message={
              <div>
                {props?.error && (
                  <Alert severity="error" style={{ marginTop: "1em" }}>
                    {props?.error.title}
                  </Alert>
                )}
                <div className="p-12 d-4 p-margin-top-2 grid-item">
                  <TextField
                    id="new-password"
                    label="Senha"
                    name="password"
                    variant="outlined"
                    type="password"
                    onChange={(e) => setNewPassword(e.target.value)}
                    onKeyDown={(e) => {
                      if (
                        e.key === "Enter" &&
                        e.shiftKey === false &&
                        e.target.value !== ""
                      ) {
                        e.preventDefault();

                        props.login({
                          login: props?.user.email,
                          password: newPassword,
                          isNewPassword: true,
                        });
                      }
                    }}
                    style={{
                      width: "100%",
                      backgroundColor: "white",
                    }}
                  />
                </div>
              </div>
            }
            buttons={[
              {
                label: "Salvar",
                onClick: () =>
                  props?.login({
                    login: props?.user.email,
                    password: newPassword,
                    isNewPassword: true,
                  }),
              },
            ]}
          />

          <Prompt
            visible={verify()}
            title="Página duplicada!"
            message={`Para evitar uma possível perda de dados, não é permitido abrir o Progerar em mais de uma aba!
              Para continuar utilizando, feche esta página e volte para utilizar a anterior.`}
            buttons={[
              {
                autoFocus: false,
              },
            ]}
          />
    </div>
  );
}

const propsFromDatabase = async (props) => {
  const loggedUser = await getLoggedUser();

  // Se não há loggedUser ou é primeiro login, não buscar user da database
  if (!loggedUser || !loggedUser.userId || loggedUser.isFirstLogin) {
    return {
      user: null,
    };
  }

  const user = await utils.getElement(
    AVAILABLE_ENTITIES.USERS,
    loggedUser.userId,
  );

  return {
    user,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    login: (params) => dispatch(login(params, true)),
  };
};

const mapStateToProps = (state) => {
  return {
    error: api.getApiError(state, `Authentication/RefreshToken`) || false,
    success: api.getApiResult(state, `Authentication/RefreshToken`) || false,
    loggedIn: state.auth.loggedIn,
    loginError: state.auth.loginError,
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(utils.withPropsFromDatabase(propsFromDatabase, withRouter(App)));
